import Phaser from 'phaser';
import { MoleType, MoleState, MoleData } from './types/MoleTypes';

export class MoleManager {
    private scene: Phaser.Scene;
    private holes: Phaser.GameObjects.Ellipse[];
    private holePositions: { x: number; y: number }[];
    private activeMoles: MoleData[] = [];
    private nextMoleTimer?: Phaser.Time.TimerEvent;
    private lockedHoles: Set<number> = new Set(); // Holes locked by frozen blue moles
    
    // Game configuration
    private minSpawnDelay = 800;
    private maxSpawnDelay = 1500;
    private moleVisibleDuration = 1000; // 1 second to catch
    private moleRiseDuration = 300;
    private moleFallDuration = 300;
    
    // Game state - game ends when 6 moles total are caught
    private blueMolesFrozen = 0; // Blue moles caught and frozen
    private redMolesCaught = 0;  // Red moles caught and frozen
    private readonly TARGET_TOTAL_MOLES = 6; // Game ends when 6 moles total (any color) are caught
    
    // Track mole spawn counts for unique IDs (for skin assignment)
    private blueMoleSpawnCount = 0; // Counter for blue mole IDs
    private redMoleSpawnCount = 0;  // Counter for red mole IDs
    
    // Track which specific mole IDs have been frozen (so they don't spawn again)
    private frozenMoleIds: Set<string> = new Set();
    
    // Game status
    private gameActive = false;
    private gameWon = false;
    private gameLost = false;
    private startTime = 0;

    constructor(scene: Phaser.Scene, holes: Phaser.GameObjects.Ellipse[], holePositions: { x: number; y: number }[]) {
        this.scene = scene;
        this.holes = holes;
        this.holePositions = holePositions;
    }

    startGame(): void {
        this.gameActive = true;
        this.gameWon = false;
        this.gameLost = false;
        this.blueMolesFrozen = 0;
        this.redMolesCaught = 0;
        this.activeMoles = [];
        this.lockedHoles.clear();
        this.blueMoleSpawnCount = 0;
        this.redMoleSpawnCount = 0;
        this.frozenMoleIds.clear();
        this.startTime = this.scene.time.now;
        
        this.scheduleNextMole();
    }

    stopGame(): void {
        this.gameActive = false;
        if (this.nextMoleTimer) {
            this.nextMoleTimer.destroy();
            this.nextMoleTimer = undefined;
        }
        // Don't clear frozen moles - they stay visible
        // Only clear non-frozen moles
        this.activeMoles.filter(m => m.state !== MoleState.FROZEN).forEach(mole => this.removeMole(mole));
    }
    
    // Ensure all timers are stopped and state is reset
    reset(): void {
        this.gameActive = false;
        this.gameWon = false;
        this.gameLost = false;
        if (this.nextMoleTimer) {
            this.nextMoleTimer.destroy();
            this.nextMoleTimer = undefined;
        }
        this.clearAllMoles();
    }
    
    clearAllMoles(): void {
        // Clear all moles including frozen ones (for game restart)
        this.activeMoles.forEach(mole => {
            if (mole.sprite) {
                mole.sprite.destroy();
            }
            if (mole.eyeGraphics) {
                mole.eyeGraphics.forEach(eye => eye.destroy());
            }
            if (mole.hatGraphics) {
                mole.hatGraphics.forEach(hat => hat.destroy());
            }
        });
        this.activeMoles = [];
        // Also clear locked holes and frozen mole IDs when clearing all moles
        this.lockedHoles.clear();
        this.frozenMoleIds.clear();
    }

    private scheduleNextMole(): void {
        if (!this.gameActive || this.gameWon || this.gameLost) return;
        
        // Keep spawning moles until 6 total moles are caught
        const totalCaught = this.blueMolesFrozen + this.redMolesCaught;
        if (totalCaught >= this.TARGET_TOTAL_MOLES) {
            return; // Game ended, stop spawning
        }

        // Random delay between spawns
        const delay = Phaser.Math.Between(this.minSpawnDelay, this.maxSpawnDelay);
        this.nextMoleTimer = this.scene.time.delayedCall(delay, () => {
            this.spawnRandomMole();
            this.scheduleNextMole();
        });
    }

    private spawnRandomMole(): void {
        if (!this.gameActive || this.gameWon || this.gameLost) return;
        
        // Find eligible holes (not locked, not occupied by any mole including frozen ones)
        // Double-check: also verify no frozen mole exists at this hole
        const occupiedHoles = new Set(this.activeMoles
            .filter(m => m.state === MoleState.VISIBLE || m.state === MoleState.POPPING || m.state === MoleState.FROZEN)
            .map(m => m.holeIndex));
        
        // Also get all holes that have frozen moles (extra safety check)
        const frozenMoleHoles = new Set(this.activeMoles
            .filter(m => m.state === MoleState.FROZEN || m.isFrozen)
            .map(m => m.holeIndex));
        
        const eligibleHoles = this.holes
            .map((_, index) => index)
            .filter(index => {
                // Must not be locked
                if (this.lockedHoles.has(index)) return false;
                // Must not be occupied by any active mole
                if (occupiedHoles.has(index)) return false;
                // Extra safety: must not have a frozen mole
                if (frozenMoleHoles.has(index)) return false;
                return true;
            });

        if (eligibleHoles.length === 0) return;

        // Randomly choose blue or red (50/50 chance)
        // But only spawn moles that haven't been frozen yet
        let moleType: MoleType;
        let moleId: string;
        let attempts = 0;
        const maxAttempts = 20; // Prevent infinite loop
        
        do {
            moleType = Phaser.Math.Between(1, 2) === 1 ? MoleType.BLUE : MoleType.RED;
            
            // Generate unique ID for this mole (cycles through 1-6 for skin assignment)
            if (moleType === MoleType.BLUE) {
                this.blueMoleSpawnCount++;
                const moleNumber = ((this.blueMoleSpawnCount - 1) % 6) + 1; // Cycle 1-6
                moleId = `BlueMole${moleNumber.toString().padStart(2, '0')}`;
            } else {
                this.redMoleSpawnCount++;
                const moleNumber = ((this.redMoleSpawnCount - 1) % 6) + 1; // Cycle 1-6
                moleId = `RedMole${moleNumber.toString().padStart(2, '0')}`;
            }
            
            attempts++;
            // If all 12 moles are frozen, break to avoid infinite loop
            if (this.frozenMoleIds.size >= 12) {
                return; // All moles frozen, can't spawn more
            }
        } while (this.frozenMoleIds.has(moleId) && attempts < maxAttempts);
        
        // If we couldn't find a non-frozen mole after max attempts, don't spawn
        if (this.frozenMoleIds.has(moleId)) {
            return;
        }

        // Randomly select an eligible hole
        const holeIndex = Phaser.Utils.Array.GetRandom(eligibleHoles);

        this.createMole(holeIndex, moleType, moleId);
    }

    private createMole(holeIndex: number, type: MoleType, id: string): void {
        try {
            const position = this.holePositions[holeIndex];
            if (!position) return;
            
            const startY = position.y + 15;
            
            // Check if this is a mole with a custom image skin
            if (id === 'BlueMole01' && this.scene.textures.exists('AOCMole')) {
                // Use image sprite for BlueMole01
                const moleSprite = this.scene.add.image(
                    position.x,
                    startY,
                    'AOCMole'
                ).setDepth(16).setVisible(true);
                
                // Scale the image to appropriate size
                moleSprite.setScale(0.5);
                
                // Add physics body
                this.scene.physics.add.existing(moleSprite);
                const physicsBody = (moleSprite.body as Phaser.Physics.Arcade.Body);
                physicsBody.setCircle(28);
                physicsBody.setImmovable(true);
                physicsBody.setAllowGravity(false);
                physicsBody.setVelocity(0, 0);
                
                const mole: MoleData = {
                    id,
                    holeIndex,
                    type,
                    state: MoleState.POPPING,
                    sprite: moleSprite,
                    eyeGraphics: [], // No eye graphics for image-based mole
                    physicsBody: physicsBody,
                    isFrozen: false
                };
                
                this.activeMoles.push(mole);
                this.animateMoleRise(mole);
                return;
            }
            
            // Check if this is BlueMole02 and use image sprite instead
            if (id === 'BlueMole02' && this.scene.textures.exists('hakeemMole')) {
                // Use image sprite for BlueMole02
                const moleSprite = this.scene.add.image(
                    position.x,
                    startY,
                    'hakeemMole'
                ).setDepth(16).setVisible(true);
                
                // Scale the image to appropriate size
                moleSprite.setScale(0.5);
                
                // Add physics body
                this.scene.physics.add.existing(moleSprite);
                const physicsBody = (moleSprite.body as Phaser.Physics.Arcade.Body);
                physicsBody.setCircle(28);
                physicsBody.setImmovable(true);
                physicsBody.setAllowGravity(false);
                physicsBody.setVelocity(0, 0);
                
                const mole: MoleData = {
                    id,
                    holeIndex,
                    type,
                    state: MoleState.POPPING,
                    sprite: moleSprite,
                    eyeGraphics: [], // No eye graphics for image-based mole
                    physicsBody: physicsBody,
                    isFrozen: false
                };
                
                this.activeMoles.push(mole);
                this.animateMoleRise(mole);
                return;
            }
            
            // Check if this is BlueMole03 and use image sprite instead
            if (id === 'BlueMole03' && this.scene.textures.exists('kamalaMole')) {
                // Use image sprite for BlueMole03
                const moleSprite = this.scene.add.image(
                    position.x,
                    startY,
                    'kamalaMole'
                ).setDepth(16).setVisible(true);
                
                // Scale the image to appropriate size
                moleSprite.setScale(0.5);
                
                // Add physics body
                this.scene.physics.add.existing(moleSprite);
                const physicsBody = (moleSprite.body as Phaser.Physics.Arcade.Body);
                physicsBody.setCircle(28);
                physicsBody.setImmovable(true);
                physicsBody.setAllowGravity(false);
                physicsBody.setVelocity(0, 0);
                
                const mole: MoleData = {
                    id,
                    holeIndex,
                    type,
                    state: MoleState.POPPING,
                    sprite: moleSprite,
                    eyeGraphics: [], // No eye graphics for image-based mole
                    physicsBody: physicsBody,
                    isFrozen: false
                };
                
                this.activeMoles.push(mole);
                this.animateMoleRise(mole);
                return;
            }
            
            // Check if this is BlueMole04 and use image sprite instead
            if (id === 'BlueMole04' && this.scene.textures.exists('pelosiMole')) {
                // Use image sprite for BlueMole04
                const moleSprite = this.scene.add.image(
                    position.x,
                    startY,
                    'pelosiMole'
                ).setDepth(16).setVisible(true);
                
                // Scale the image to appropriate size
                moleSprite.setScale(0.5);
                
                // Add physics body
                this.scene.physics.add.existing(moleSprite);
                const physicsBody = (moleSprite.body as Phaser.Physics.Arcade.Body);
                physicsBody.setCircle(28);
                physicsBody.setImmovable(true);
                physicsBody.setAllowGravity(false);
                physicsBody.setVelocity(0, 0);
                
                const mole: MoleData = {
                    id,
                    holeIndex,
                    type,
                    state: MoleState.POPPING,
                    sprite: moleSprite,
                    eyeGraphics: [], // No eye graphics for image-based mole
                    physicsBody: physicsBody,
                    isFrozen: false
                };
                
                this.activeMoles.push(mole);
                this.animateMoleRise(mole);
                return;
            }
            
            // Check if this is BlueMole05 and use image sprite instead
            if (id === 'BlueMole05' && this.scene.textures.exists('chuckMole')) {
                // Use image sprite for BlueMole05
                const moleSprite = this.scene.add.image(
                    position.x,
                    startY,
                    'chuckMole'
                ).setDepth(16).setVisible(true);
                
                // Scale the image to appropriate size
                moleSprite.setScale(0.5);
                
                // Add physics body
                this.scene.physics.add.existing(moleSprite);
                const physicsBody = (moleSprite.body as Phaser.Physics.Arcade.Body);
                physicsBody.setCircle(28);
                physicsBody.setImmovable(true);
                physicsBody.setAllowGravity(false);
                physicsBody.setVelocity(0, 0);
                
                const mole: MoleData = {
                    id,
                    holeIndex,
                    type,
                    state: MoleState.POPPING,
                    sprite: moleSprite,
                    eyeGraphics: [], // No eye graphics for image-based mole
                    physicsBody: physicsBody,
                    isFrozen: false
                };
                
                this.activeMoles.push(mole);
                this.animateMoleRise(mole);
                return;
            }
            
            // Check if this is BlueMole06 and use image sprite instead
            if (id === 'BlueMole06' && this.scene.textures.exists('warrenMole')) {
                // Use image sprite for BlueMole06
                const moleSprite = this.scene.add.image(
                    position.x,
                    startY,
                    'warrenMole'
                ).setDepth(16).setVisible(true);
                
                // Scale the image to appropriate size
                moleSprite.setScale(0.5);
                
                // Add physics body
                this.scene.physics.add.existing(moleSprite);
                const physicsBody = (moleSprite.body as Phaser.Physics.Arcade.Body);
                physicsBody.setCircle(28);
                physicsBody.setImmovable(true);
                physicsBody.setAllowGravity(false);
                physicsBody.setVelocity(0, 0);
                
                const mole: MoleData = {
                    id,
                    holeIndex,
                    type,
                    state: MoleState.POPPING,
                    sprite: moleSprite,
                    eyeGraphics: [], // No eye graphics for image-based mole
                    physicsBody: physicsBody,
                    isFrozen: false
                };
                
                this.activeMoles.push(mole);
                this.animateMoleRise(mole);
                return;
            }
            
            // Check if this is RedMole01 and use image sprite instead
            if (id === 'RedMole01' && this.scene.textures.exists('bondiMole')) {
                // Use image sprite for RedMole01
                const moleSprite = this.scene.add.image(
                    position.x,
                    startY,
                    'bondiMole'
                ).setDepth(16).setVisible(true);
                
                // Scale the image to appropriate size
                moleSprite.setScale(0.5);
                
                // Add physics body
                this.scene.physics.add.existing(moleSprite);
                const physicsBody = (moleSprite.body as Phaser.Physics.Arcade.Body);
                physicsBody.setCircle(28);
                physicsBody.setImmovable(true);
                physicsBody.setAllowGravity(false);
                physicsBody.setVelocity(0, 0);
                
                const mole: MoleData = {
                    id,
                    holeIndex,
                    type,
                    state: MoleState.POPPING,
                    sprite: moleSprite,
                    eyeGraphics: [], // No eye graphics for image-based mole
                    physicsBody: physicsBody,
                    isFrozen: false
                };
                
                this.activeMoles.push(mole);
                this.animateMoleRise(mole);
                return;
            }
            
            // Check if this is RedMole02 and use image sprite instead
            if (id === 'RedMole02' && this.scene.textures.exists('kashMole')) {
                // Use image sprite for RedMole02
                const moleSprite = this.scene.add.image(
                    position.x,
                    startY,
                    'kashMole'
                ).setDepth(16).setVisible(true);
                
                // Scale the image to appropriate size
                moleSprite.setScale(0.5);
                
                // Add physics body
                this.scene.physics.add.existing(moleSprite);
                const physicsBody = (moleSprite.body as Phaser.Physics.Arcade.Body);
                physicsBody.setCircle(28);
                physicsBody.setImmovable(true);
                physicsBody.setAllowGravity(false);
                physicsBody.setVelocity(0, 0);
                
                const mole: MoleData = {
                    id,
                    holeIndex,
                    type,
                    state: MoleState.POPPING,
                    sprite: moleSprite,
                    eyeGraphics: [], // No eye graphics for image-based mole
                    physicsBody: physicsBody,
                    isFrozen: false
                };
                
                this.activeMoles.push(mole);
                this.animateMoleRise(mole);
                return;
            }
            
            // Check if this is RedMole03 and use image sprite instead
            if (id === 'RedMole03' && this.scene.textures.exists('melaniaMole')) {
                // Use image sprite for RedMole03
                const moleSprite = this.scene.add.image(
                    position.x,
                    startY,
                    'melaniaMole'
                ).setDepth(16).setVisible(true);
                
                // Scale the image to appropriate size
                moleSprite.setScale(0.5);
                
                // Add physics body
                this.scene.physics.add.existing(moleSprite);
                const physicsBody = (moleSprite.body as Phaser.Physics.Arcade.Body);
                physicsBody.setCircle(28);
                physicsBody.setImmovable(true);
                physicsBody.setAllowGravity(false);
                physicsBody.setVelocity(0, 0);
                
                const mole: MoleData = {
                    id,
                    holeIndex,
                    type,
                    state: MoleState.POPPING,
                    sprite: moleSprite,
                    eyeGraphics: [], // No eye graphics for image-based mole
                    physicsBody: physicsBody,
                    isFrozen: false
                };
                
                this.activeMoles.push(mole);
                this.animateMoleRise(mole);
                return;
            }
            
            // Check if this is RedMole04 and use image sprite instead
            if (id === 'RedMole04' && this.scene.textures.exists('rfkMole')) {
                // Use image sprite for RedMole04
                const moleSprite = this.scene.add.image(
                    position.x,
                    startY,
                    'rfkMole'
                ).setDepth(16).setVisible(true);
                
                // Scale the image to appropriate size
                moleSprite.setScale(0.5);
                
                // Add physics body
                this.scene.physics.add.existing(moleSprite);
                const physicsBody = (moleSprite.body as Phaser.Physics.Arcade.Body);
                physicsBody.setCircle(28);
                physicsBody.setImmovable(true);
                physicsBody.setAllowGravity(false);
                physicsBody.setVelocity(0, 0);
                
                const mole: MoleData = {
                    id,
                    holeIndex,
                    type,
                    state: MoleState.POPPING,
                    sprite: moleSprite,
                    eyeGraphics: [], // No eye graphics for image-based mole
                    physicsBody: physicsBody,
                    isFrozen: false
                };
                
                this.activeMoles.push(mole);
                this.animateMoleRise(mole);
                return;
            }
            
            // Check if this is RedMole05 and use image sprite instead
            if (id === 'RedMole05' && this.scene.textures.exists('trumpMole')) {
                // Use image sprite for RedMole05
                const moleSprite = this.scene.add.image(
                    position.x,
                    startY,
                    'trumpMole'
                ).setDepth(16).setVisible(true);
                
                // Scale the image to appropriate size
                moleSprite.setScale(0.5);
                
                // Add physics body
                this.scene.physics.add.existing(moleSprite);
                const physicsBody = (moleSprite.body as Phaser.Physics.Arcade.Body);
                physicsBody.setCircle(28);
                physicsBody.setImmovable(true);
                physicsBody.setAllowGravity(false);
                physicsBody.setVelocity(0, 0);
                
                const mole: MoleData = {
                    id,
                    holeIndex,
                    type,
                    state: MoleState.POPPING,
                    sprite: moleSprite,
                    eyeGraphics: [], // No eye graphics for image-based mole
                    physicsBody: physicsBody,
                    isFrozen: false
                };
                
                this.activeMoles.push(mole);
                this.animateMoleRise(mole);
                return;
            }
            
            // Check if this is RedMole06 and use image sprite instead
            if (id === 'RedMole06' && this.scene.textures.exists('vanceMole')) {
                // Use image sprite for RedMole06
                const moleSprite = this.scene.add.image(
                    position.x,
                    startY,
                    'vanceMole'
                ).setDepth(16).setVisible(true);
                
                // Scale the image to appropriate size
                moleSprite.setScale(0.5);
                
                // Add physics body
                this.scene.physics.add.existing(moleSprite);
                const physicsBody = (moleSprite.body as Phaser.Physics.Arcade.Body);
                physicsBody.setCircle(28);
                physicsBody.setImmovable(true);
                physicsBody.setAllowGravity(false);
                physicsBody.setVelocity(0, 0);
                
                const mole: MoleData = {
                    id,
                    holeIndex,
                    type,
                    state: MoleState.POPPING,
                    sprite: moleSprite,
                    eyeGraphics: [], // No eye graphics for image-based mole
                    physicsBody: physicsBody,
                    isFrozen: false
                };
                
                this.activeMoles.push(mole);
                this.animateMoleRise(mole);
                return;
            }
            
            // Don't create default moles - only use custom image skins
            // If image doesn't exist yet, don't create the mole (it will spawn later when image loads)
            // Silently skip - images are loading in background
            return;
        } catch (error) {
            console.error('Error in createMole:', error);
        }
    }

    private animateMoleRise(mole: MoleData): void {
        if (!mole.sprite || mole.isFrozen || mole.state === MoleState.FROZEN) return;
        
        const position = this.holePositions[mole.holeIndex];
        const startHeadY = position.y + 15;
        const endHeadY = position.y - 40;
        
        const yOffset = endHeadY - startHeadY;
        
        // Animate mole head
        this.scene.tweens.add({
            targets: mole.sprite,
            y: endHeadY,
            duration: this.moleRiseDuration,
            ease: 'Back.easeOut'
        });
        
        // Animate other parts
        const targets: Phaser.GameObjects.GameObject[] = [];
        if (mole.eyeGraphics) {
            targets.push(...mole.eyeGraphics);
        }

        targets.forEach(target => {
            if (target instanceof Phaser.GameObjects.Image || target instanceof Phaser.GameObjects.Sprite) {
                this.scene.tweens.add({
                    targets: target,
                    y: target.y + yOffset,
                    duration: this.moleRiseDuration,
                    ease: 'Back.easeOut'
                });
            } else if (target instanceof Phaser.GameObjects.GameObject && 'y' in target) {
                this.scene.tweens.add({
                    targets: target,
                    y: (target as any).y + yOffset,
                    duration: this.moleRiseDuration,
                    ease: 'Back.easeOut'
                });
            }
        });
        
        // Set state to visible when animation completes
        this.scene.tweens.add({
            targets: mole.sprite,
            y: endHeadY,
            duration: this.moleRiseDuration,
            ease: 'Back.easeOut',
            onComplete: () => {
                // Only set to visible if not frozen
                if (!mole.isFrozen && mole.state !== MoleState.FROZEN && mole.state === MoleState.POPPING) {
                    mole.state = MoleState.VISIBLE;
                    this.startMoleVisibleTimer(mole);
                }
            }
        });
    }

    private startMoleVisibleTimer(mole: MoleData): void {
        mole.visibleTimer = this.scene.time.delayedCall(this.moleVisibleDuration, () => {
            // Double-check state and frozen status before falling
            if (mole.state === MoleState.VISIBLE && !mole.isFrozen) {
                this.animateMoleFall(mole);
            }
        });
    }

    private animateMoleFall(mole: MoleData): void {
        // Don't fall if frozen or already gone
        if (!mole.sprite || mole.isFrozen || mole.state === MoleState.FROZEN || mole.state === MoleState.GONE) return;
        
        mole.state = MoleState.HIDDEN;
        const position = this.holePositions[mole.holeIndex];
        const endHeadY = position.y + 15;
        
        const startHeadY = mole.sprite.y;
        const yOffset = endHeadY - startHeadY;
        
        const targets: Phaser.GameObjects.GameObject[] = [mole.sprite];
        if (mole.eyeGraphics) {
            targets.push(...mole.eyeGraphics);
        }

        targets.forEach(target => {
            if (target instanceof Phaser.GameObjects.Image || target instanceof Phaser.GameObjects.Sprite) {
                this.scene.tweens.add({
                    targets: target,
                    y: target.y + yOffset,
                    duration: this.moleFallDuration,
                    ease: 'Back.easeIn'
                });
            } else if (target instanceof Phaser.GameObjects.GameObject && 'y' in target) {
                this.scene.tweens.add({
                    targets: target,
                    y: (target as any).y + yOffset,
                    duration: this.moleFallDuration,
                    ease: 'Back.easeIn'
                });
            }
        });
        
        this.scene.tweens.add({
            targets: mole.sprite,
            y: endHeadY,
            duration: this.moleFallDuration,
            ease: 'Back.easeIn',
            onComplete: () => {
                this.removeMole(mole);
            }
        });
    }

    // Check if hat touches a mole (generous collision detection for easier catching)
    checkHatCollision(hatX: number, hatY: number, hatRadius: number): MoleData | null {
        let closestMole: MoleData | null = null;
        let closestDistance = Infinity;
        
        // Check all moles and find the closest one that's hit
        for (const mole of this.activeMoles) {
            if (!mole.sprite || !mole.physicsBody || mole.isFrozen) continue;
            if (mole.state !== MoleState.VISIBLE && mole.state !== MoleState.POPPING) continue;

            const moleX = mole.sprite.x;
            const moleY = mole.sprite.y;
            const moleRadius = 28;
            
            // Generous collision detection - larger hit area for easier catching
            const hitAreaMultiplier = 1.5; // 50% larger hit area
            const effectiveMoleRadius = moleRadius * hitAreaMultiplier;
            const effectiveHatRadius = hatRadius * hitAreaMultiplier;
            
            // Simple distance check - if hat touches mole (with generous area)
            const distance = Phaser.Math.Distance.Between(hatX, hatY, moleX, moleY);
            const minDistance = effectiveHatRadius + effectiveMoleRadius;

            if (distance < minDistance && distance < closestDistance) {
                closestMole = mole;
                closestDistance = distance;
            }
        }
        
        return closestMole;
    }

    // Handle mole caught by hat - simple freeze logic
    catchMole(mole: MoleData, _hatX: number, _hatY: number): void {
        // Safety check
        if (!mole || !mole.sprite || mole.isFrozen || mole.state === MoleState.FROZEN || mole.state === MoleState.GONE) {
            return;
        }

        // Mark as frozen FIRST to prevent any further processing
        mole.isFrozen = true;
        mole.state = MoleState.FROZEN;

        // Cancel all timers
        try {
            if (mole.visibleTimer) {
                mole.visibleTimer.destroy();
                mole.visibleTimer = undefined;
            }
            if (mole.riseTimer) {
                mole.riseTimer.destroy();
                mole.riseTimer = undefined;
            }
            if (mole.fallTimer) {
                mole.fallTimer.destroy();
                mole.fallTimer = undefined;
            }
        } catch (e) {
            // Ignore timer errors
        }

        // Stop ALL animations
        try {
            if (mole.sprite) {
                this.scene.tweens.killTweensOf(mole.sprite);
            }
            if (mole.eyeGraphics) {
                mole.eyeGraphics.forEach(g => {
                    if (g) {
                        this.scene.tweens.killTweensOf(g);
                    }
                });
            }
        } catch (e) {
            // Ignore tween errors
        }

        // Move mole to fully upright position before freezing
        const position = this.holePositions[mole.holeIndex];
        const fullyVisibleY = position.y - 40; // Fully visible/erect position
        
        if (mole.sprite) {
            const currentY = mole.sprite.y;
            const yOffset = fullyVisibleY - currentY;
            
            // Instantly move mole to fully upright position
            mole.sprite.setY(fullyVisibleY);
            
            // Move all mole parts to match (maintain relative positions)
            if (mole.eyeGraphics) {
                mole.eyeGraphics.forEach(g => {
                    if (g && g.active) {
                        g.setY(g.y + yOffset);
                    }
                });
            }
        }
        
        if (mole.type === MoleType.BLUE) {
            // Blue mole - freeze it and add hat
            const moleX = mole.sprite.x;
            const moleY = fullyVisibleY; // Use the fully upright position
            
            // Lock the hole so no more moles spawn here
            this.lockedHoles.add(mole.holeIndex);
            this.blueMolesFrozen++;
            
            // Play sound when blue mole freezes
            try {
                if (this.scene.cache.audio.exists('hitdem')) {
                    this.scene.sound.play('hitdem');
                }
            } catch (e) {
                // Sound not loaded yet, ignore
            }
            
            // Mark this mole ID as frozen so it won't spawn again
            this.frozenMoleIds.add(mole.id);
            
            // Special handling for moles with custom images - switch to sombrero version
            if (mole.sprite && mole.sprite instanceof Phaser.GameObjects.Image) {
                if (mole.id === 'BlueMole01' && this.scene.textures.exists('AOCSombrero')) {
                    // Switch BlueMole01 to sombrero version
                    mole.sprite.setTexture('AOCSombrero');
                    mole.sprite.setScale(0.5);
                    mole.sprite.clearTint(); // No color tint
                    mole.hatGraphics = []; // Image already has sombrero
                } else if (mole.id === 'BlueMole02' && this.scene.textures.exists('hakeemSombrero')) {
                    // Switch BlueMole02 to sombrero version
                    mole.sprite.setTexture('hakeemSombrero');
                    mole.sprite.setScale(0.5);
                    mole.sprite.clearTint(); // No color tint
                    mole.hatGraphics = []; // Image already has sombrero
                } else if (mole.id === 'BlueMole03' && this.scene.textures.exists('kamalaSombrero')) {
                    // Switch BlueMole03 to sombrero version
                    mole.sprite.setTexture('kamalaSombrero');
                    mole.sprite.setScale(0.5);
                    mole.sprite.clearTint(); // No color tint
                    mole.hatGraphics = []; // Image already has sombrero
                } else if (mole.id === 'BlueMole04' && this.scene.textures.exists('pelosiSombrero')) {
                    // Switch BlueMole04 to sombrero version
                    mole.sprite.setTexture('pelosiSombrero');
                    mole.sprite.setScale(0.5);
                    mole.sprite.clearTint(); // No color tint
                    mole.hatGraphics = []; // Image already has sombrero
                } else if (mole.id === 'BlueMole05' && this.scene.textures.exists('chuckSombrero')) {
                    // Switch BlueMole05 to sombrero version
                    mole.sprite.setTexture('chuckSombrero');
                    mole.sprite.setScale(0.5);
                    mole.sprite.clearTint(); // No color tint
                    mole.hatGraphics = []; // Image already has sombrero
                } else if (mole.id === 'BlueMole06' && this.scene.textures.exists('warrenSombrero')) {
                    // Switch BlueMole06 to sombrero version
                    mole.sprite.setTexture('warrenSombrero');
                    mole.sprite.setScale(0.5);
                    mole.sprite.clearTint(); // No color tint
                    mole.hatGraphics = []; // Image already has sombrero
                } else {
                    // For other image-based moles, just remove tint
                    if (mole.sprite && 'clearTint' in mole.sprite && typeof (mole.sprite as any).clearTint === 'function') {
                        (mole.sprite as any).clearTint();
                    }
                }
            } else {
                // For other blue moles, create hat graphics
                try {
                    const hatBase = this.scene.add.circle(moleX, moleY - 25, 22, 0x654321).setDepth(20);
                    const hatBody = this.scene.add.circle(moleX, moleY - 25, 20, 0x8B4513).setDepth(21);
                    const hatHighlight = this.scene.add.circle(moleX - 5, moleY - 30, 12, 0xB8860B, 0.5).setDepth(22);
                    const hatRim = this.scene.add.circle(moleX, moleY - 25, 20, 0x654321, 0)
                        .setStrokeStyle(3, 0x3a2a1a).setDepth(21);
                    
                    mole.hatGraphics = [hatBase, hatBody, hatHighlight, hatRim];
                    
                    // No tint - display as-is (removed blue tint)
                    if (mole.sprite instanceof Phaser.GameObjects.Image || mole.sprite instanceof Phaser.GameObjects.Sprite) {
                        mole.sprite.clearTint();
                    }
                } catch (e) {
                    console.error('Error creating hat on mole:', e);
                }
            }
            
            // Check if game should end (6 total moles caught)
            const totalCaught = this.blueMolesFrozen + this.redMolesCaught;
            if (totalCaught >= this.TARGET_TOTAL_MOLES) {
                this.gameWon = true;
                // Delay stopGame to next frame to prevent freeze
                this.scene.time.delayedCall(100, () => {
                    this.stopGame();
                });
            }
        } else {
            // Red mole - freeze it same as blue mole
            const moleX = mole.sprite.x;
            const moleY = fullyVisibleY; // Use the fully upright position
            
            // Mark this mole ID as frozen so it won't spawn again
            this.frozenMoleIds.add(mole.id);
            
            // Lock the hole so no more moles spawn here
            this.lockedHoles.add(mole.holeIndex);
            this.redMolesCaught++;
            
            // Play sound when red mole freezes
            try {
                if (this.scene.cache.audio.exists('hitgop')) {
                    this.scene.sound.play('hitgop');
                }
            } catch (e) {
                // Sound not loaded yet, ignore
            }
            
            // Special handling for red moles with custom images - switch to sombrero version
            if (mole.sprite && mole.sprite instanceof Phaser.GameObjects.Image) {
                if (mole.id === 'RedMole01' && this.scene.textures.exists('bondiSombrero')) {
                    // Switch RedMole01 to sombrero version
                    mole.sprite.setTexture('bondiSombrero');
                    mole.sprite.setScale(0.5);
                    mole.sprite.clearTint(); // No color tint
                    mole.hatGraphics = []; // Image already has sombrero
                } else if (mole.id === 'RedMole02' && this.scene.textures.exists('kashSombrero')) {
                    // Switch RedMole02 to sombrero version
                    mole.sprite.setTexture('kashSombrero');
                    mole.sprite.setScale(0.5);
                    mole.sprite.clearTint(); // No color tint
                    mole.hatGraphics = []; // Image already has sombrero
                } else if (mole.id === 'RedMole03' && this.scene.textures.exists('melaniaSombrero')) {
                    // Switch RedMole03 to sombrero version
                    mole.sprite.setTexture('melaniaSombrero');
                    mole.sprite.setScale(0.5);
                    mole.sprite.clearTint(); // No color tint
                    mole.hatGraphics = []; // Image already has sombrero
                } else if (mole.id === 'RedMole04' && this.scene.textures.exists('rfkSombrero')) {
                    // Switch RedMole04 to sombrero version
                    mole.sprite.setTexture('rfkSombrero');
                    mole.sprite.setScale(0.5);
                    mole.sprite.clearTint(); // No color tint
                    mole.hatGraphics = []; // Image already has sombrero
                } else if (mole.id === 'RedMole05' && this.scene.textures.exists('trumpSombrero')) {
                    // Switch RedMole05 to sombrero version
                    mole.sprite.setTexture('trumpSombrero');
                    mole.sprite.setScale(0.5);
                    mole.sprite.clearTint(); // No color tint
                    mole.hatGraphics = []; // Image already has sombrero
                    
                    // Play sound when Trump mole freezes
                    try {
                        if (this.scene.cache.audio.exists('muymalo')) {
                            this.scene.sound.play('muymalo');
                        }
                    } catch (e) {
                        // Sound not loaded yet, ignore
                    }
                } else if (mole.id === 'RedMole06' && this.scene.textures.exists('vanceSombrero')) {
                    // Switch RedMole06 to sombrero version
                    mole.sprite.setTexture('vanceSombrero');
                    mole.sprite.setScale(0.5);
                    mole.sprite.clearTint(); // No color tint
                    mole.hatGraphics = []; // Image already has sombrero
                } else {
                    // For other image-based red moles, just remove tint
                    if (mole.sprite && 'clearTint' in mole.sprite && typeof (mole.sprite as any).clearTint === 'function') {
                        (mole.sprite as any).clearTint();
                    }
                    // Create programmatic hat
                    try {
                        const hatBase = this.scene.add.circle(moleX, moleY - 25, 22, 0x654321).setDepth(20);
                        const hatBody = this.scene.add.circle(moleX, moleY - 25, 20, 0x8B4513).setDepth(21);
                        const hatHighlight = this.scene.add.circle(moleX - 5, moleY - 30, 12, 0xB8860B, 0.5).setDepth(22);
                        const hatRim = this.scene.add.circle(moleX, moleY - 25, 20, 0x654321, 0)
                            .setStrokeStyle(3, 0x3a2a1a).setDepth(21);
                        mole.hatGraphics = [hatBase, hatBody, hatHighlight, hatRim];
                    } catch (e) {
                        console.error('Error creating hat on red mole:', e);
                    }
                }
            } else {
                // For non-image red moles, create programmatic hat
                try {
                    const hatBase = this.scene.add.circle(moleX, moleY - 25, 22, 0x654321).setDepth(20);
                    const hatBody = this.scene.add.circle(moleX, moleY - 25, 20, 0x8B4513).setDepth(21);
                    const hatHighlight = this.scene.add.circle(moleX - 5, moleY - 30, 12, 0xB8860B, 0.5).setDepth(22);
                    const hatRim = this.scene.add.circle(moleX, moleY - 25, 20, 0x654321, 0)
                        .setStrokeStyle(3, 0x3a2a1a).setDepth(21);
                    mole.hatGraphics = [hatBase, hatBody, hatHighlight, hatRim];
                    if (mole.sprite instanceof Phaser.GameObjects.Image || mole.sprite instanceof Phaser.GameObjects.Sprite) {
                        mole.sprite.clearTint();
                    }
                } catch (e) {
                    console.error('Error creating hat on red mole:', e);
                }
            }
            
            // Check if game should end (6 total moles caught)
            const totalCaught = this.blueMolesFrozen + this.redMolesCaught;
            if (totalCaught >= this.TARGET_TOTAL_MOLES) {
                this.gameWon = true;
                // Delay stopGame to next frame to prevent freeze
                this.scene.time.delayedCall(100, () => {
                    this.stopGame();
                });
            }
        }
    }

    private removeMole(mole: MoleData): void {
        // Don't remove frozen moles - they stay visible with their hat
        if (mole.state === MoleState.FROZEN) return;
        
        if (mole.sprite) {
            mole.sprite.destroy();
        }
        if (mole.eyeGraphics) {
            mole.eyeGraphics.forEach(eye => eye.destroy());
        }
        if (mole.hatGraphics) {
            mole.hatGraphics.forEach(hat => hat.destroy());
        }
        const index = this.activeMoles.indexOf(mole);
        if (index > -1) {
            this.activeMoles.splice(index, 1);
        }
    }

    // Getters
    getBlueMolesFrozen(): number {
        return this.blueMolesFrozen;
    }

    getRedMolesCaught(): number {
        return this.redMolesCaught;
    }

    getTotalBlueMoles(): number {
        return this.blueMolesFrozen;
    }

    getTotalRedMoles(): number {
        return this.redMolesCaught;
    }
    
    getTotalCaught(): number {
        return this.blueMolesFrozen + this.redMolesCaught;
    }
    
    getTargetTotal(): number {
        return this.TARGET_TOTAL_MOLES;
    }

    getElapsedTime(): number {
        if (!this.startTime) return 0;
        return this.scene.time.now - this.startTime;
    }

    calculateScore(): number {
        const elapsedMs = this.getElapsedTime();
        const baseScore = 10000;
        const timePenalty = elapsedMs / 100; // Much slower penalty (1 point per 100ms instead of 10ms)
        const redPenalty = this.redMolesCaught * 1000;
        return Math.max(0, baseScore - timePenalty - redPenalty);
    }

    isGameActive(): boolean {
        return this.gameActive;
    }

    isGameWon(): boolean {
        return this.gameWon;
    }

    isGameLost(): boolean {
        return this.gameLost;
    }
}
